import axios from "axios";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import { ArrowUpDown, Edit2, Eye, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DataTable } from "../components/DataTable";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { BaseUrlApi, ErrorMessage } from "../lib/api";
import SkeletonTable from "./SkeletonTable";
import { useNavigate } from "react-router-dom";
import StarRating from "../components/StarRating";
import { useAuth } from "../context/AuthContext";
import { DialogDelete } from "../components/Common/DialogDelete";

export default function Projects() {
  const { user, loading } = useAuth();

  const navigate = useNavigate();

  const ApiUrl =
    !loading && user?.role === "admin" ? `/projects` : `/user/projects`;

  // Projects
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projects, setProjects] = useState([]);
  const handleFetchProjects = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}${ApiUrl}`);
      setProjects(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    setLoadingProjects(true);
    handleFetchProjects();
  }, []);

  const getProgressStyle = (progress) => {
    const value = parseFloat(progress);
    if (value < 50) {
      return "text-red-600";
    } else if (value < 80) {
      return "text-amber-600";
    } else {
      return "text-green-600";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "in_progress":
        return (
          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
            جاري التنفيذ
          </Badge>
        );
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            مكتمل
          </Badge>
        );
      case "pending":
        return (
          <Badge
            variant="outline"
            className="bg-amber-100 text-amber-800 hover:bg-amber-100"
          >
            قيد الانتظار
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const columns = [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          اسم المشروع
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => row.original.name || "___",
    },
    {
      accessorKey: "category.title",
      header: "الفئة",
      cell: ({ row }) => row.original.category?.title || "___",
    },
    {
      accessorKey: "date_start",
      header: "تاريخ البدء",
      cell: ({ row }) =>
        row.original.date_start
          ? dayjs(row.original.date_start).format("DD/MM/YYYY hh:mm A")
          : "___",
    },
    {
      accessorKey: "date_end",
      header: "تاريخ الانتهاء",
      cell: ({ row }) =>
        row.original.date_end
          ? dayjs(row.original.date_end).format("DD/MM/YYYY hh:mm A")
          : "___",
    },
    {
      accessorKey: "progress",
      header: "النسبة المكتملة",
      cell: ({ row }) => {
        const progress = row.original.progress || "0.00";
        return <span className={getProgressStyle(progress)}>{progress}%</span>;
      },
    },
    {
      accessorKey: "team.name",
      header: "اسم الفريق",
      cell: ({ row }) => row.original.team?.name || "___",
    },
    {
      accessorKey: "review",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          التقييم
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => <StarRating rating={row.original.review} />,
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          الحالة
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => getStatusBadge(row.original.status),
    },
    {
      id: "Actions",
      header: "الإجراءات",
      cell: ({ row }) => (
        <>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="rounded-lg px-2 border-none text-yellow-600"
            >
              <Eye />
            </Button>
            {!loading && user?.role === "admin" && (
              <>
                <Button
                  size="sm"
                  className="rounded-lg px-2 text-green-600"
                  variant="ghost"
                  onClick={() => navigate(`/projects-edit/${row.original.bid}`)}
                >
                  <Edit2 />
                </Button>
                <DialogDelete
                  title={"المشروع"}
                  data={row.original.name}
                  apiUrl={`/projects/${row.original.bid}`}
                  fetchData={handleFetchProjects}
                />
              </>
            )}
          </div>
        </>
      ),
    },
  ];

  const exportData = projects.map((row) => ({
    name: row.name || "___",
    category: row.category?.title || "___",
    date_start: new Date(row.date_start).toLocaleString("ar-EG", {
      dateStyle: "medium",
      timeStyle: "short",
    }),
    date_end: new Date(row.date_end).toLocaleString("ar-EG", {
      dateStyle: "medium",
      timeStyle: "short",
    }),
    progress: row.progress ? `${row.progress}%` : "___",
    review: row.review,
    status: row.status,
    team: row.team?.name || "___",
  }));

  const exportColumns = [
    { accessorKey: "name", header: "اسم المشروع" },
    { accessorKey: "category", header: "الفئة" },
    { accessorKey: "date_start", header: "تاريخ البدء" },
    { accessorKey: "date_end", header: "تاريخ الانتهاء" },
    { accessorKey: "progress", header: "النسبة المكتملة" },
    { accessorKey: "review", header: "التقييم" },
    { accessorKey: "status", header: "الحالة" },
    { accessorKey: "team", header: "اسم الفريق" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
      className="space-y-5"
    >
      <div className="flex items-center gap-2 justify-between">
        <h1 className="text-2xl font-bold">المشاريع</h1>
        {!loading && user?.role === "admin" ? (
          <Button onClick={() => navigate("/projects-add")}>إضافة مشروع</Button>
        ) : null}
      </div>

      {loadingProjects ? (
        <SkeletonTable />
      ) : (
        <DataTable
          columns={columns}
          data={projects}
          title={"المشاريع"}
          filterNames={["name", "category_title", "status"]}
          exportData={exportData}
          exportColumns={exportColumns}
        />
      )}
    </motion.div>
  );
}
